import { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { ACCESS_TOKEN_COOKIE } from '@r1-frontend/shared/constants';
import Cookies from '@r1-frontend/shared/helpers/Cookies';
import { AGREEMENT_NUMBER } from '@r1-frontend/shared/helpers/getAgreementNumber';
import { isClientSide } from '@r1-frontend/shared/helpers/ssr';
import { getIsPlayStation } from '@r1-frontend/shared/helpers/userAgent/getIsPlayStation';

const CHAT_HOST = process.env.NEXT_PUBLIC_HOST_CHAT || '/';
const MAX_ATTEMPTS = 10;

type Props = {
    children: ReactNode,
    enable?: boolean,
    channel?: string,
    initiator?: string,
}

export const ChatCtx = createContext<{ chatInstance: ChatFrame | null }>({
    chatInstance: null,
});

const ChatContext = ({ children, enable = true, channel = 'Web_chat', initiator = 'B2C-DOMRU' }: Props) => {
    const [chatInstance, setChatInstance] = useState<ChatFrame | null>(null);
    const { query } = useRouter();

    const attempt = useRef(0);

    const thisPlayStation = isClientSide() && getIsPlayStation(window.navigator.userAgent);

    useEffect(() => {
        if (thisPlayStation) {
            return;
        }

        const cookies = new Cookies();

        const userId = cookies.get(AGREEMENT_NUMBER);
        const accessToken = cookies.get(ACCESS_TOKEN_COOKIE);
        const citydomain = cookies.get('citydomain');

        const interval = setInterval(() => {
            attempt.current++;

            if (window.ChatFrame && !chatInstance && attempt.current < MAX_ATTEMPTS) {
                const chatFrame = new window.ChatFrame({
                    enable,
                    channel,
                    initiator,
                    host: CHAT_HOST,
                    userData: { citydomain, userId, accessToken },
                });
                setChatInstance(chatFrame);

                if (typeof query.openchat === 'string') {
                    chatFrame.open();
                }

                clearInterval(interval);
                attempt.current = 0;
            }
        }, 100);

        return () => {
            clearInterval(interval);
        };
    }, []);

    if (thisPlayStation) {
        return <>{children}</>;
    }

    return <ChatCtx.Provider value={{ chatInstance }}>
        {children}
    </ChatCtx.Provider>;
};

export default ChatContext;
